<template>
  <nav
    v-if="shouldShowBreadcrumbs"
    aria-label="Breadcrumbs"
    class="breadcrumbs"
    role="navigation"
  >
    <ul>
      <li>
        <NuxtLink to="/"> Home </NuxtLink>
        <span class="divide"> / </span>
      </li>
      <template v-if="ancestors.length">
        <NavigationBreadcrumbsAncestor
          v-for="ancestor in ancestors"
          :key="ancestor.uuid"
          :blok="ancestor"
        />
      </template>
    </ul>
  </nav>
</template>

<script setup lang="ts">
const ancestors = ref([])
const route = useRoute()
const { story } = storeToRefs(useStoryProviderStore())

const shouldShowBreadcrumbs = computed(() => {
  const normalizedPath = route.path.replace(/\/+$/, '')
  return !(route.params.slug === '' || normalizedPath === '/search')
})

const parentFullSlugs = computed(() => {
  const slugs: string[] =
    story.value.full_slug
      ?.split('/')
      ?.filter((string) => string && string !== story.value.slug) || []

  let path
  return slugs.map((slug, index, array) => {
    path = index >= 1 ? path + array[index] + '/' : slug + '/'
    return path
  })
})

async function fetch() {
  if (!parentFullSlugs.value.length) return
  const { data } = await useStoryblokHelpers().fetchAsyncData('stories', {
    by_slugs: parentFullSlugs.value.join(','),
  })
  // Re-order ancestors so that it matches order of parentFullSlugs
  const parentSlugs = Object.values(parentFullSlugs)
  ancestors.value = data.value.data.stories.sort(
    (a, b) =>
      parentSlugs.indexOf(b.slug + '/') - parentSlugs.indexOf(a.slug + '/'),
  )
}

await fetch()
</script>

<style lang="less">
.breadcrumbs {
  padding: 0 0 @gutter-xxs;

  @media @tablet {
    padding-top: @gutter-sm;
  }

  ul {
    .font-xxs();
    .list-reset();
    .block();

    li {
      .inline();

      a {
        .font-medium();
        .inline-block();

        &:link,
        &:visited {
          color: @color-text;
        }
        &:hover,
        &:active {
          color: @color-primary;
        }

        background-image: linear-gradient(
          90deg,
          @color-primary,
          @color-primary
        );
        background-position: 0 100%;
        background-repeat: no-repeat;
        background-size: 100% @2px;
        padding: 0 0 @4px;
        margin: 0 @6px @6px 0;

        @media @desktop {
          padding-bottom: @6px;
        }
      }

      span.divide {
        padding: 0 @6px 0 0;
        .text-muted();
      }

      span.current {
        .font-regular();
        .text-normal();
        .inline-block();
      }
    }
  }

  // stylelint-disable -- descending specificity
  .page-intro--basic &,
  .page-intro--with-media & {
    a {
      &:link,
      &:visited {
        color: @color-text-reverse;
      }
      &:hover,
      &:active {
        color: @color-primary;
      }
    }

    span.current {
      .text-reverse();
    }
  }
  // stylelint-enable
}
</style>
